import React, { useState, useMemo, useEffect } from 'react';
import { TouchableOpacity, View, Text, StyleSheet } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { FontAwesome5 } from '@expo/vector-icons';
import { BasicLayout, CommonContainer } from '../../../../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH2, BOHTHead, BOHTR, BOHTable } from '../../../../../../common/components/bohtable';
import { BOHButton, BOHToolbar } from '../../../../../../common/components/bohtoolbar';
import { TextMediumSize } from '../../../../../../common/constants/Fonts';
import AddPenaltyRuleModal from '../../modals/AddPenalityRuleModal';
import { getPenaltyRules, createPenaltyRule, updatePenaltyRule, deletePenaltyRule } from '../../../../../../api/Penality';
import { COLUMN_WIDTHS } from '../../../../../../common/constants/ColumnWidths';
import { useAlertModal, useConfirmModal } from '../../../../../../common/hooks';

const PenaltyRules = ({ navigation, goBack }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterAppliesTo, setFilterAppliesTo] = useState('');
  const [filterCalculationType, setFilterCalculationType] = useState('');

  // Fetch penalty rules on component mount
  useEffect(() => {
    fetchPenaltyRules();
  }, []);

  const fetchPenaltyRules = async () => {
    setIsLoading(true);
    await getPenaltyRules({}, (jsonRes, status, error) => {
      setIsLoading(false);
      if (status === 200 && jsonRes?.success) {
        // Sort by type and applies_to
        const sortedData = (jsonRes.data || []).sort((a: any, b: any) => {
          // First by type
          if (a.type < b.type) return -1;
          if (a.type > b.type) return 1;
          
          // Then by applies_to (all, hourly, daily, weekly, monthly)
          const unitOrder = { all: 1, hourly: 2, daily: 3, weekly: 4, monthly: 5 };
          const orderA = unitOrder[a.applies_to] || 6;
          const orderB = unitOrder[b.applies_to] || 6;
          if (orderA !== orderB) return orderA - orderB;
          
          // Finally by rule name
          return a.rule_name.localeCompare(b.rule_name);
        });
        
        setTableData(sortedData);
      } else {
        showAlert('Error', error || 'Failed to fetch penalty rules');
      }
    });
  };


  // Get unique values for filters
  const uniqueTypes = useMemo(() => {
    const types = [...new Set(tableData.map(item => item.type))];
    return types.sort();
  }, [tableData]);

  const uniqueAppliesTo = useMemo(() => {
    const appliesTo = [...new Set(tableData.map(item => item.applies_to))];
    return appliesTo.sort();
  }, [tableData]);

  const uniqueCalculationTypes = useMemo(() => {
    const calcTypes = [...new Set(tableData.map(item => item.calculation_type))];
    return calcTypes.sort();
  }, [tableData]);

  // Filter table data based on selected filters
  const filteredData = useMemo(() => {
    return tableData.filter(item => {
      const typeMatch = !filterType || item.type === filterType;
      const appliesToMatch = !filterAppliesTo || item.applies_to === filterAppliesTo;
      const calcTypeMatch = !filterCalculationType || item.calculation_type === filterCalculationType;
      return typeMatch && appliesToMatch && calcTypeMatch;
    });
  }, [tableData, filterType, filterAppliesTo, filterCalculationType]);

  const openAddModal = () => {
    setSelectedRule(null);
    setAddModalVisible(true);
  };

  const editRule = (item) => {
    setSelectedRule(item);
    setAddModalVisible(true);
  };

  const deleteRule = async (id) => {
    showConfirm(
      'Are you sure you want to delete this penalty rule?',
      async () => {
        setIsLoading(true);
        await deletePenaltyRule({ id }, (jsonRes, status, error) => {
          setIsLoading(false);
          if (status === 200 && jsonRes?.success) {
            showAlert('Success', 'Penalty rule deleted successfully');
            fetchPenaltyRules();
          } else {
            showAlert('Error', jsonRes?.message || error || 'Failed to delete penalty rule');
          }
        });
      }
    );
  };

  const toggleActive = async (id) => {
    const rule = tableData.find(item => item.id === id);
    if (rule) {
      setIsLoading(true);
      await updatePenaltyRule({ ...rule, active: rule.active ? 0 : 1 }, (jsonRes, status, error) => {
        setIsLoading(false);
        if (status === 200 && jsonRes?.success) {
          fetchPenaltyRules();
        } else {
          showAlert('Error', jsonRes?.message || error || 'Failed to update penalty rule');
        }
      });
    }
  };

  const renderTableData = useMemo(() => {
    const rows = [];
    if (filteredData.length > 0) {
      filteredData.map((item, index) => {
        rows.push(
          <BOHTR key={index}>
            <BOHTD width={COLUMN_WIDTHS.EXTRA_WIDE}>{item.rule_name}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.LARGE}>{item.type.charAt(0).toUpperCase() + item.type.slice(1)}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.LARGE}>{item.applies_to.charAt(0).toUpperCase() + item.applies_to.slice(1)}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.LARGE}>{item.calculation_type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.PRICE}>${parseFloat(item.amount).toFixed(2)}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.MEDIUM}>{item.min_value ? parseFloat(item.min_value).toFixed(2) : '-'}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.MEDIUM}>{item.max_value ? parseFloat(item.max_value).toFixed(2) : '-'}</BOHTD>
            <BOHTD width={COLUMN_WIDTHS.EXTRA_LARGE}>{item.description}</BOHTD>
            <BOHTDIconBox width={COLUMN_WIDTHS.ICON}>
              <TouchableOpacity onPress={() => editRule(item)}>
                <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
            <BOHTDIconBox width={COLUMN_WIDTHS.ICON}>
              <TouchableOpacity onPress={() => deleteRule(item.id)}>
                <FontAwesome5 size={TextMediumSize} name="times" color="red" />
              </TouchableOpacity>
            </BOHTDIconBox>
          </BOHTR>
        );
      });
    }
    return <>{rows}</>;
  }, [filteredData]);

  const tableElement = useMemo(() => (
    <BOHTable isLoading={isLoading}>
      <BOHTHead>
        <BOHTR>
          <BOHTH2 width={COLUMN_WIDTHS.EXTRA_WIDE}>Rule Name</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.LARGE}>Type</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.LARGE}>Applies To</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.LARGE}>Calculation</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.PRICE}>Amount</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.MEDIUM}>Min Value</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.MEDIUM}>Max Value</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.EXTRA_LARGE}>Description</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.ICON}>Edit</BOHTH2>
          <BOHTH2 width={COLUMN_WIDTHS.ICON}>Delete</BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>{renderTableData}</BOHTBody>
    </BOHTable>
  ), [isLoading, renderTableData]);

  return (
    <BasicLayout navigation={navigation} goBack={goBack} screenName={'Penalty Rules'}>
      <CommonContainer>
        <BOHToolbar>
          <BOHButton label="Add Penalty" onPress={openAddModal} />
          <View style={styles.filterContainer}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Type:</Text>
              <Picker
                selectedValue={filterType}
                onValueChange={(value) => setFilterType(value)}
                style={styles.filterPicker}
              >
                <Picker.Item label="All Types" value="" />
                {uniqueTypes.map((type) => (
                  <Picker.Item key={type} label={type} value={type} />
                ))}
              </Picker>
            </View>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Applies To:</Text>
              <Picker
                selectedValue={filterAppliesTo}
                onValueChange={(value) => setFilterAppliesTo(value)}
                style={styles.filterPicker}
              >
                <Picker.Item label="All" value="" />
                {uniqueAppliesTo.map((applies) => (
                  <Picker.Item key={applies} label={applies} value={applies} />
                ))}
              </Picker>
            </View>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Calculation:</Text>
              <Picker
                selectedValue={filterCalculationType}
                onValueChange={(value) => setFilterCalculationType(value)}
                style={styles.filterPicker}
              >
                <Picker.Item label="All Types" value="" />
                {uniqueCalculationTypes.map((calcType) => (
                  <Picker.Item key={calcType} label={calcType} value={calcType} />
                ))}
              </Picker>
            </View>
            {(filterType || filterAppliesTo || filterCalculationType) && (
              <TouchableOpacity 
                onPress={() => {
                  setFilterType('');
                  setFilterAppliesTo('');
                  setFilterCalculationType('');
                }}
                style={styles.clearButton}
              >
                <Text style={styles.clearButtonText}>Clear Filters</Text>
              </TouchableOpacity>
            )}
          </View>
        </BOHToolbar>
        {tableElement}
        <AddPenaltyRuleModal
          isModalVisible={isAddModalVisible}
          details={selectedRule}
          closeModal={() => setAddModalVisible(false)}
          onSave={async (rule) => {
            setIsLoading(true);
            
            if (selectedRule) {
              await updatePenaltyRule(rule, async (jsonRes, status, error) => {
                if (status === 200 && jsonRes?.success) {
                  showAlert('Success', 'Penalty rule updated successfully');
                  setAddModalVisible(false);
                  await fetchPenaltyRules();
                } else {
                  showAlert('Error', jsonRes?.message || error || 'Failed to update penalty rule');
                }
                setIsLoading(false);
              });
            } else {
              await createPenaltyRule(rule, async (jsonRes, status, error) => {
                if (status === 201 && jsonRes?.success) {
                  showAlert('Success', 'Penalty rule created successfully');
                  setAddModalVisible(false);
                  await fetchPenaltyRules();
                } else {
                  showAlert('Error', jsonRes?.message || error || 'Failed to create penalty rule');
                }
                setIsLoading(false);
              });
            }
          }}
        />
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    marginLeft: 20,
    flex: 1,
  },
  filterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterPicker: {
    width: 150,
    height: 40,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
  },
  clearButton: {
    backgroundColor: '#ff6b6b',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    marginLeft: 10,
  },
  clearButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default PenaltyRules;
